import { Text, TextButton } from "@clipboard-health/ui-components";
import { zodResolver } from "@hookform/resolvers/zod";
// TODO: Use our own Input component when available
// eslint-disable-next-line no-restricted-imports -- TextField is needed for multiline input
import { DialogContent, Stack, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "../../../components/Button";
import { DialogFooter } from "../../../components/DialogFooter";

const contactInfoSchema = z.object({
  contactName: z.string().optional(),
  contactEmail: z
    .string()
    .optional()
    .refine((value) => !value || z.string().email().safeParse(value).success, {
      message: "Please enter a valid email",
    }),
  contactPhone: z.string().optional(),
  comments: z.string().optional(),
});

export type ContactInfoFormValues = z.infer<typeof contactInfoSchema>;

interface ContactInfoSheetProps {
  onSubmit: (data: ContactInfoFormValues) => void;
  onSkip: () => void;
  onBack: () => void;
}

export function ContactInfoSheet({ onSubmit, onSkip, onBack }: ContactInfoSheetProps) {
  const methods = useForm<ContactInfoFormValues>({
    resolver: zodResolver(contactInfoSchema),
    defaultValues: {
      contactName: "",
      contactEmail: "",
      contactPhone: "",
      comments: "",
    },
  });

  const { handleSubmit, control } = methods;

  return (
    <Stack>
      <DialogContent>
        <Stack spacing={2} alignItems="center">
          <Text semibold variant="h3" sx={{ textAlign: "center" }}>
            Know someone at this facility?
          </Text>
          <Text
            variant="body1"
            color={(theme) => theme.palette.text.secondary}
            sx={{ textAlign: "center" }}
          >
            If you have a contact at the facility, it can help us expedite onboarding and get them
            on Clipboard faster—so you can start booking shifts here sooner!
          </Text>
        </Stack>

        <Stack spacing={6} sx={(theme) => ({ marginTop: theme.spacing(2) })}>
          <Stack direction="column" spacing={3}>
            <Text semibold variant="body1" sx={{ paddingLeft: 1 }}>
              Contact Name (Optional)
            </Text>

            <Controller
              name="contactName"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <TextField
                    {...field}
                    fullWidth
                    id="contact-name-input"
                    placeholder="e.g., John Smith"
                    variant="outlined"
                    size="medium"
                    error={Boolean(fieldState.error)}
                  />

                  {fieldState.error && (
                    <Text
                      variant="caption"
                      color={(theme) => theme.palette.intent?.error.text}
                      sx={{ paddingLeft: 1 }}
                    >
                      {fieldState.error.message}
                    </Text>
                  )}
                </>
              )}
            />
          </Stack>

          <Stack direction="column" spacing={3}>
            <Text semibold variant="body1" sx={{ paddingLeft: 1 }}>
              Contact Email (Optional)
            </Text>

            <Controller
              name="contactEmail"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <TextField
                    {...field}
                    fullWidth
                    id="contact-email-input"
                    placeholder="e.g., john@facility.com"
                    type="email"
                    variant="outlined"
                    size="medium"
                    error={Boolean(fieldState.error)}
                  />

                  {fieldState.error && (
                    <Text
                      variant="caption"
                      color={(theme) => theme.palette.intent?.error.text}
                      sx={{ paddingLeft: 1 }}
                    >
                      {fieldState.error.message}
                    </Text>
                  )}
                </>
              )}
            />
          </Stack>

          <Stack direction="column" spacing={3}>
            <Text semibold variant="body1" sx={{ paddingLeft: 1 }}>
              Contact Phone (Optional)
            </Text>

            <Controller
              name="contactPhone"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <TextField
                    {...field}
                    fullWidth
                    id="contact-phone-input"
                    placeholder="e.g., (555) 123-4567"
                    variant="outlined"
                    size="medium"
                    error={Boolean(fieldState.error)}
                  />

                  {fieldState.error && (
                    <Text
                      variant="caption"
                      color={(theme) => theme.palette.intent?.error.text}
                      sx={{ paddingLeft: 1 }}
                    >
                      {fieldState.error.message}
                    </Text>
                  )}
                </>
              )}
            />
          </Stack>

          <Stack direction="column" spacing={3}>
            <Text semibold variant="body1" sx={{ paddingLeft: 1 }}>
              Comments (Optional)
            </Text>

            <Controller
              name="comments"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <TextField
                    {...field}
                    multiline
                    fullWidth
                    id="comments-input"
                    placeholder="Works in the Rehab unit, great staff!"
                    rows={3}
                    variant="outlined"
                    size="medium"
                    error={Boolean(fieldState.error)}
                  />

                  {fieldState.error && (
                    <Text
                      variant="caption"
                      color={(theme) => theme.palette.intent?.error.text}
                      sx={{ paddingLeft: 1 }}
                    >
                      {fieldState.error.message}
                    </Text>
                  )}
                </>
              )}
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogFooter orientation="vertical">
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleSubmit((data: ContactInfoFormValues) => {
            onSubmit(data);
          })}
        >
          Continue
        </Button>
        <TextButton size="large" LinkComponent="button" onClick={onSkip}>
          Skip for now
        </TextButton>
        <TextButton size="large" LinkComponent="button" onClick={onBack}>
          Back
        </TextButton>
      </DialogFooter>
    </Stack>
  );
}
